import * as Slider from "@radix-ui/react-slider";
import Head from "next/head";
import React, { useState } from "react";
import CurrencyInput from "react-currency-input-field";
import { AiOutlinePercentage } from "react-icons/ai";
import { HiOutlineBanknotes } from "react-icons/hi2";
import { MdOutlineTimelapse } from "react-icons/md";

import { getOGimage, getUrl } from "../../utils/getUrl";

function MortgageCalculator() {
  const [homeValue, setHomeValue] = useState(4000000);
  const [interestRate, setInterestRate] = useState(12);
  const [period, setPeriod] = useState(5);
  const [downPayment, setDownPayment] = useState(350000);
  const [downPercentage, setDownPercentage] = useState(
    parseInt((downPayment / homeValue) * 100).toFixed(0)
  );

  const getMonthlyPayment = () => {
    if (downPayment === homeValue) {
      return 0;
    }
    // if(downPayment> homeValue){
    //   // return 0
    // }
    else {
      let loanAmount = homeValue - downPayment;
      let monthlyInterestRate = interestRate / 100 / 12;
      let numberOfPayments = period * 12;
      let monthlyPayment =
        (loanAmount * monthlyInterestRate) /
        (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments));
      return Intl.NumberFormat().format(monthlyPayment.toFixed(0));
    }
  };
  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta charSet="utf-8" />
        <title>Kenya Mortgage Calculator | Zote</title>
        <link
          rel="canonical"
          href={`${getUrl()}/calculators/mortgage-calculator`}
        />
        <meta
          name="description"
          content="Take the first step towards homeownership in Kenya with this Mortgage Calculator.
          Customise home price, interest rate, and more to get monthly mortgage payments"
        />
        <meta name="og:title" content="Kenya Mortgage Calculator | Zote" />
        <meta
          name="og:description"
          content="Take the first step towards homeownership in Kenya with this Mortgage Calculator.
          Customise home price, interest rate, and more to get monthly mortgage payments"
        />
        <meta
          property="og:url"
          content={`${getUrl()}/calculators/mortgage-calculator`}
        />

        <meta name="og:image" content={`${getOGimage()}`} />
      </Head>
      <main className=" flex flex-col space-y-8 px-5 py-8 lg:px-24 lg:py-16">
        <div className=" flex flex-col space-y-0">
          <h1 className=" text-2xl font-bold">Kenya Mortgage Calculator</h1>
          <h2 className=" text-gray-700">
            Take the first step towards homeownership with our Mortgage
            Calculator
          </h2>
        </div>
        <div className=" flex w-full flex-col rounded-md bg-blue-50 lg:h-96 lg:flex-row">
          <div className=" order-2 grid w-full grid-cols-1 gap-y-4 rounded-l-md px-5 py-5 lg:order-1 lg:w-1/2 lg:grid-cols-2 lg:gap-5 lg:gap-y-0 lg:px-8 lg:py-8">
            <div className=" flex h-32 flex-col space-y-1">
              <div className=" flex flex-col">
                <span className=" font-medium">Home price</span>
                <span className=" text-gray-600">Enter home price</span>
              </div>

              <CurrencyInput
                id="homevalue"
                name="homevalue"
                placeholder=""
                className="rounded-md border border-gray-300 bg-white px-5 py-2"
                allowDecimals={false}
                allowNegativeValue={false}
                min={0}
                value={homeValue}
                prefix="KES "
                onValueChange={(value, name) => {
                  if (!value) return setHomeValue(0);
                  if (value > 20000000) {
                    setHomeValue(20000000);
                    return;
                  }
                  setHomeValue(value);
                }}
              />
              <Slider.Root
                className="   relative flex h-5 w-full touch-none select-none items-center"
                min={0}
                max={40000000}
                step={1000}
                defaultValue={[homeValue]}
                value={[homeValue]}
                onValueCommit={(value) => {
                  if (value[0][0] === 0) {
                    setDownPayment(0);
                    setDownPercentage(0);
                  }
                  if (downPercentage) {
                    setDownPayment(((downPercentage / 100) * value).toFixed(0));
                  }
                }}
                onValueChange={(value) => {
                  setHomeValue(value);
                  if (downPercentage) {
                    setDownPayment(((downPercentage / 100) * value).toFixed(0));
                  }
                }}
              >
                <Slider.Track className=" flex w-full">
                  <Slider.Range className=" flex  h-1 w-full rounded-full bg-zote-secondary " />
                </Slider.Track>
                <Slider.Thumb
                  className="  flex h-3 w-3 cursor-grab rounded-[10px] bg-blue-400  outline-none"
                  aria-label="Sales Price"
                />
              </Slider.Root>
            </div>
            <div className=" flex h-32 flex-col space-y-1">
              <div className=" flex flex-col">
                <span className=" font-medium">Down Payment</span>
                <span className=" text-gray-600">
                  Enter payment value or percentage
                </span>
              </div>

              <div className=" flex flex-row space-x-2 ">
                <CurrencyInput
                  id="downvalue"
                  name="downvalue"
                  placeholder=""
                  className=" w-4/6 rounded-md border border-gray-300 bg-white px-5 py-2"
                  allowDecimals={false}
                  allowNegativeValue={false}
                  min={0}
                  max={downPayment}
                  value={downPayment}
                  prefix="KES "
                  onValueChange={(value, name) => {
                    if (!value) return setDownPayment(0);
                    if (value > 40000000) {
                      setDownPayment(homeValue);
                      setDownPercentage(100);
                      return;
                    }
                    if (value > homeValue) {
                      setDownPercentage(100);
                      setDownPayment(homeValue);
                    } else {
                      setDownPercentage(
                        parseInt((value / homeValue) * 100)
                          .toFixed(0)
                          .toString()
                      );
                      setDownPayment(value);
                    }
                  }}
                />
                <CurrencyInput
                  id="interest"
                  name="interest"
                  placeholder=""
                  className="w-2/6 rounded-md border border-gray-300 bg-white px-5 py-2"
                  allowDecimals={false}
                  allowNegativeValue={false}
                  min={0}
                  max={parseInt((downPayment / homeValue) * 100).toFixed(0)}
                  value={downPercentage}
                  suffix="%"
                  onValueChange={(value, name) => {
                    if (!value) return setDownPercentage(0);
                    if (parseInt(value).toFixed(0) > 100) {
                      setDownPercentage(100);
                      setDownPayment(homeValue);
                    } else {
                      setDownPercentage(parseInt(value).toFixed(0).toString());
                      setDownPayment(
                        (parseInt(value).toFixed(0) / 100) * homeValue
                      );
                    }
                  }}
                />
              </div>
              <Slider.Root
                className="   relative flex h-5 w-full touch-none select-none items-center"
                min={0}
                max={100}
                step={1}
                defaultValue={[downPercentage]}
                value={[downPercentage]}
                onValueChange={(value) => {
                  // setDownPayment(value);
                  setDownPayment(
                    ((parseInt(value[0]) / 100) * homeValue).toFixed(0)
                  );
                  // console.log(downPayment);
                  setDownPercentage(value[0].toFixed(0));
                }}
              >
                <Slider.Track className=" flex w-full">
                  <Slider.Range className=" flex  h-1 w-full rounded-full bg-zote-secondary " />
                </Slider.Track>
                <Slider.Thumb
                  className="  flex h-3 w-3 cursor-grab rounded-[10px] bg-blue-400  outline-none"
                  aria-label="Sales Price"
                />
              </Slider.Root>
            </div>
            <div className=" flex h-32 flex-col space-y-1">
              <div className=" flex flex-col">
                <span className=" font-medium">Term</span>
                <span className=" text-gray-600">Enter loan length</span>
              </div>

              <select
                onChange={(e) => {
                  setPeriod(e.target.value);
                }}
                defaultValue={period}
                className=" rounded-md border border-gray-300 bg-white px-5 py-2"
              >
                <option value={25}>25 Years</option>
                <option value={20}>20 Years</option>
                <option value={15}>15 Years</option>
                <option value={10}>10 Years</option>
                <option value={5}>5 Years</option>
              </select>
            </div>
            <div className=" flex h-32 flex-col space-y-1">
              <div className=" flex flex-col">
                <span className=" font-medium">Interest Rate</span>
                <span className=" text-gray-600">
                  Rates may vary by instituition
                </span>
              </div>

              <CurrencyInput
                id="interest"
                name="interest"
                placeholder=""
                className="rounded-md border border-gray-300 bg-white px-5 py-2"
                allowDecimals={true}
                allowNegativeValue={false}
                min={0}
                max={25}
                value={interestRate}
                suffix="%"
                onValueChange={(value, name) => {
                  if (!value) return setInterestRate(0);
                  if (value > 25) {
                    setInterestRate(25);
                  } else {
                    setInterestRate(value);
                  }
                }}
              />
              <Slider.Root
                className="   relative flex h-5 w-full touch-none select-none items-center"
                min={0}
                max={25}
                step={0.01}
                defaultValue={[interestRate]}
                value={[interestRate]}
                onValueChange={(value) => {
                  setInterestRate(value);
                }}
              >
                <Slider.Track className=" flex w-full">
                  <Slider.Range className=" flex  h-1 w-full rounded-full bg-zote-secondary " />
                </Slider.Track>
                <Slider.Thumb
                  className="  flex h-3 w-3 cursor-grab rounded-[10px] bg-blue-400  outline-none"
                  aria-label="Sales Price"
                />
              </Slider.Root>
            </div>
          </div>
          <div className=" relative order-1 flex w-full  flex-col rounded-r-md lg:order-2 lg:w-1/2 lg:flex-row">
            <div className="sticky top-0 flex w-full items-center justify-center rounded-t-md bg-zote-primary py-5 text-gray-100 lg:relative lg:top-auto lg:w-1/2 lg:rounded-none lg:py-0">
              <div className=" flex flex-col items-center lg:space-y-1">
                <span className=" text-3xl font-semibold lg:text-4xl">
                  KES {getMonthlyPayment()}
                </span>
                <span className=" text-base font-medium">per month</span>
              </div>
            </div>
            <div className=" flex w-full flex-row justify-between bg-zote-background px-5 py-5 text-gray-900 lg:w-7/12 lg:flex-col  lg:justify-center lg:space-y-3 lg:rounded-r-md lg:px-10 lg:py-0">
              <div className=" flex flex-col items-start space-y-1 lg:flex-row lg:items-center lg:space-x-3">
                <div className=" flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-zote-primary">
                  <HiOutlineBanknotes className=" h-5 w-5 text-gray-100" />
                </div>
                <div className=" flex flex-col">
                  <span className=" font-semibold">Down Payment</span>
                  <span className=" text-sm">
                    KES {Intl.NumberFormat().format(downPayment)}
                  </span>
                </div>
              </div>
              <div className=" flex flex-col space-y-1 lg:flex-row lg:items-center lg:space-x-3">
                <div className=" flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md  bg-zote-primary">
                  <AiOutlinePercentage className=" h-5 w-5 text-gray-100" />
                </div>
                <div className=" flex flex-col">
                  <span className=" font-semibold">Interest Rate</span>
                  <span className=" text-sm">
                    {Intl.NumberFormat().format(interestRate)}%
                  </span>
                </div>
              </div>
              <div className=" flex flex-col space-y-1 lg:flex-row lg:items-center lg:space-x-3">
                <div className="  flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md  bg-zote-primary">
                  <MdOutlineTimelapse className=" h-5 w-5 text-gray-100" />
                </div>
                <div className=" flex flex-col">
                  <span className=" font-semibold">Term</span>
                  <span className=" text-sm">{period} Years</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default MortgageCalculator;
